import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Typography,
} from "@mui/material";

import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { NightReport } from "interfaces/nightReport.interface";

import { NightReportField, NightReportFieldType } from "./NightReportField";

const FIELD_TYPE_DISPLAY_ORDER: NightReportFieldType[] = [
  "RESPIRATION_RATE",
  "MOVEMENT",
  "TIME_ASLEEP",
  "OUT_OF_BED",
  "SLEEP_SCORE",
  "SESSION_IS_IN_PROGRESS",
  "BASELINE_NIGHT_COUNT",
  "REPORT_LINK",
];

interface NightReportAccordionProps {
  reports: NightReport[];
}

export const NightReportAccordion = ({
  reports,
}: NightReportAccordionProps) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ width: "100%" }}>
      {reports.map((report: NightReport) => (
        <Accordion
          key={report.subject?.id} // TODO: use integration_user_id
          sx={{
            boxShadow: "none",
            "&.Mui-expanded": { margin: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown />}
            id={`panel${report.subject?.id}-header`}
            sx={{
              backgroundColor: "backgrounds.neutral.soft.default",
              height: "56px",
              paddingLeft: 3,
              "&.Mui-expanded": {
                backgroundColor: "backgrounds.neutral.soft.pressed",
                minHeight: 0,
              },
            }}
          >
            <Box sx={{ width: "161px" }}>
              <Typography variant="bodyMicroRegular">
                {report.device?.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="bodyMicroRegular">
                {report.subject?.identifier}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Box sx={{ backgroundColor: "backgrounds.neutral.soft.default" }}>
              {report.state === "NO_SLEEP" && (
                <NightReportNoDataMessage
                  message={t("No sleep data recorded due to not enough sleep")}
                />
              )}
              {report.state === "NO_PRESENCE" && (
                <NightReportNoDataMessage
                  message={t("subject_not_present_or_data_not_available")}
                />
              )}
              {report.state === "PRESENCE_AND_SLEEP" && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "1px",
                  }}
                >
                  {FIELD_TYPE_DISPLAY_ORDER.map((fieldType) => (
                    <Box
                      key={fieldType}
                      sx={{
                        height: "64px",
                        backgroundColor: "backgrounds.static.neutral",
                        display: "flex",
                        alignItems: "center",
                        px: 2.5,
                        py: 1.25,
                        boxSizing: "border-box",
                      }}
                    >
                      <NightReportField type={fieldType} report={report} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      <Card
        sx={{
          height: "40px",
          borderTop: "1px solid",
          borderColor: "borders.neutral.subtle",
          borderRadius: "0 0 8px 8px",
          boxShadow: "none",
          pr: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          color: "texts.neutral.subtle",
        }}
      >
        <Typography variant="bodySmallMedium">
          {t("device_count", { count: reports.length })}
        </Typography>
      </Card>
    </Box>
  );
};

type NightReportNoDataMessageProps = {
  message: string;
};

export function NightReportNoDataMessage({
  message,
}: NightReportNoDataMessageProps) {
  return (
    <Box sx={{ px: 2.5, py: 1.25, color: "texts.neutral.subtle" }}>
      <Typography variant="bodyMicroRegular">{message}</Typography>
    </Box>
  );
}
